import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { server } from "../../server";
import ProfileFeatureWidgetActionMenu from "../Profile/ProfileFeatureWidgetActionMenu";
import ProfileImageGalleryModal from "./ProfileImageGalleryModal";
import ProfileGalleryPostActions from "./ProfileGalleryPostActions";

const USER_PHOTO_TABS = [
  { id: "all", label: "All" },
  { id: "post", label: "Posts", origins: ["post"] },
  { id: "profile", label: "Profile", origins: ["avatar"] },
  { id: "banner", label: "Banner", origins: ["banner"] },
  { id: "meme", label: "Memes", origins: ["meme"] },
  { id: "other", label: "Uploads" },
];

const HIDDEN_PROFILE_IMAGE_ORIGINS = new Set([
  "ad",
  "custom-ad",
  "custom_ad",
  "ad-format",
  "ad_format",
  "ad-campaign",
  "ad_campaign",
  "advertisement",
  "sponsored",
  "sponsored-post",
  "sponsored_post",
  "product",
  "event",
  "event-avatar",
  "barcode",
  "nutrition",
  "roombanner",
  "roomavatar",
  "brand",
  "brand_catalog_item",
  "message",
  "site-logo",
]);

const getNormalizedImageUrl = (image = {}) => String(image.url || "").split("?")[0].trim().toLowerCase();

const getImageOrigin = (image = {}) => String(image.origin || (image.postId ? "post" : "other")).toLowerCase();

const isGalleryEligibleImage = (image = {}) => Boolean(image.url) && !HIDDEN_PROFILE_IMAGE_ORIGINS.has(getImageOrigin(image));

const isImageInTab = (image, tab) => {
  if (tab.id === "all") {
    return true;
  }

  const origin = getImageOrigin(image);
  if (tab.id === "other") {
    return !USER_PHOTO_TABS.some((candidate) => candidate.origins?.includes(origin));
  }

  return tab.origins?.includes(origin);
};

const dedupeImagesByUrl = (imageList = []) => {
  const seen = new Set();

  return imageList.filter((image, index) => {
    const key = getNormalizedImageUrl(image) || String(image._id || image.key || index);
    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const MyImagesProfile = ({
  userId,
  canAddPhotos = false,
  emptyMessage = "No photos yet.",
  emptyActionHref,
  ownerName = "User",
  ownerAvatar,
}) => {
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activePhotoTab, setActivePhotoTab] = useState("all");
  const photoTabsRef = useRef(null);

  useEffect(() => {
    const fetchImages = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${server}/user/user/${userId}/images`);
        setImages(Array.isArray(response.data.images) ? response.data.images : []);
      } catch (error) {
        console.error("Error fetching images", error);
        setImages([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImages();
  }, [userId]);

  const galleryImages = images.filter(isGalleryEligibleImage);
  const uniqueImages = dedupeImagesByUrl(galleryImages);
  const photoTabs = USER_PHOTO_TABS.map((tab) => ({
    ...tab,
    count: dedupeImagesByUrl(galleryImages.filter((image) => isImageInTab(image, tab))).length,
  })).filter((tab) => tab.id === "all" || tab.count > 0);
  const activeTab = photoTabs.some((tab) => tab.id === activePhotoTab) ? activePhotoTab : "all";
  const activeTabConfig = USER_PHOTO_TABS.find((tab) => tab.id === activeTab) || USER_PHOTO_TABS[0];
  const activeImages = dedupeImagesByUrl(galleryImages.filter((image) => isImageInTab(image, activeTabConfig)));

  useEffect(() => {
    setCurrentImageIndex(0);
    setShowModal(false);
  }, [activePhotoTab]);

  const openModal = (index = 0) => {
    if (activeImages.length === 0) {
      return;
    }

    setCurrentImageIndex(index);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const previousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? activeImages.length - 1 : prevIndex - 1
    );
  };

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === activeImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const handleDeleteImage = (deletedImageId) => {
    setImages((currentImages) => currentImages.filter((image) => String(image.imageId || image._id || image.id) !== String(deletedImageId)));
    setShowModal(false);
    setCurrentImageIndex(0);
  };

  const scrollPhotoTabs = (direction) => {
    photoTabsRef.current?.scrollBy({
      left: direction * 180,
      behavior: "smooth",
    });
  };

  const hasImages = uniqueImages.length > 0;
  const hasActiveImages = activeImages.length > 0;
  const photoActions = [
    {
      label: "Add Photos",
      href: emptyActionHref,
      hidden: !canAddPhotos || !emptyActionHref,
    },
    {
      label: "See All",
      onClick: () => openModal(),
      hidden: !hasActiveImages,
    },
  ];
  const hasPhotoActions = photoActions.some((action) => !action.hidden);
  const modal =
    showModal && hasImages ? (
      <ProfileImageGalleryModal
        images={activeImages}
        currentImageIndex={Math.min(currentImageIndex, activeImages.length - 1)}
        onClose={closeModal}
        onPrevious={previousImage}
        onNext={nextImage}
        onGoToImage={goToImage}
        ownerName={ownerName}
        ownerAvatar={ownerAvatar}
        profileType="User"
        onDeleteImage={handleDeleteImage}
      />
    ) : null;

  return (
    <div className="my-images-profile-component">
      <div className="my-images-profile-header">
        <h3>Photos</h3>
        {hasPhotoActions && (
          <ProfileFeatureWidgetActionMenu label="Photo actions" actions={photoActions} />
        )}
      </div>
      {hasImages && (
        <div className="profile-photos-source-tabs-shell">
          <button
            type="button"
            className="profile-photos-source-tabs-arrow profile-photos-source-tabs-arrow--prev"
            onClick={() => scrollPhotoTabs(-1)}
            aria-label="Scroll photo sources back"
          >
            &#10094;
          </button>
          <div className="profile-photos-source-tabs" ref={photoTabsRef} aria-label="Photo sources">
            {photoTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`profile-photos-source-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActivePhotoTab(tab.id)}
            >
              <span>{tab.label}</span>
              <strong>{tab.id === "all" ? uniqueImages.length : tab.count}</strong>
            </button>
            ))}
          </div>
          <button
            type="button"
            className="profile-photos-source-tabs-arrow profile-photos-source-tabs-arrow--next"
            onClick={() => scrollPhotoTabs(1)}
            aria-label="Scroll photo sources forward"
          >
            &#10095;
          </button>
        </div>
      )}
      
      {hasActiveImages ? (
        <div className="my-images-profile-image-grid">
          {activeImages.map((image, index) => (
            <article className="profile-photo-thumbnail-card" key={image._id || image.url || index}>
              <button
                type="button"
                className="profile-photo-thumbnail-button"
                onClick={() => openModal(index)}
                aria-label={`Open user image ${index + 1}`}
              >
                <img
                  src={image.url}
                  alt={`User uploaded ${index + 1}`}
                  className="my-images-profile-thumbnail profile-photo-thumbnail"
                />
              </button>
              <ProfileGalleryPostActions
                image={image}
                ownerName={ownerName}
                ownerAvatar={ownerAvatar}
                profileType="User"
                className="profile-photo-thumbnail-actions"
                buttonClassName="profile-photo-thumbnail-action"
                ariaLabel={`Engagement for user image ${index + 1}`}
              />
            </article>
          ))}
        </div>
      ) : isLoading ? (
        <div className="profile-widget-loading-state">
          <p className="profile-widget-loading-message">Loading photos...</p>
        </div>
      ) : (
        <div className="profile-widget-empty-state">
          <p className="profile-widget-empty-message">{emptyMessage}</p>
          {canAddPhotos && emptyActionHref && (
            <a className="profile-widget-empty-action" href={emptyActionHref}>
              Add Photos
            </a>
          )}
        </div>
      )}

      {modal && typeof document !== "undefined"
        ? createPortal(modal, document.body)
        : null}
    </div>
  );
};

export default MyImagesProfile;
